import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Visitor {
  id: string
  visitor_name: string
  company: string | null
  visit_type: string
  host_name: string
  scheduled_date: string
  scheduled_time: string
  status: string
  badge_number: string | null
  location: string
}

interface AccessLog {
  id: string
  badge_number: string
  visitor_name: string
  access_point: string
  action: string
  timestamp: string
  granted: boolean
}

export default function VisitorsPage() {
  const [visitors, setVisitors] = useState<Visitor[]>([])
  const [accessLogs, setAccessLogs] = useState<AccessLog[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [visRes, logsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/visitors/'),
      fetch(API_BASE + '/visitors/access-logs'),
      fetch(API_BASE + '/visitors/statistics')
    ])
    if (visRes.ok) setVisitors((await visRes.json()).visitors || [])
    if (logsRes.ok) setAccessLogs((await logsRes.json()).access_logs || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'checked_in': return 'bg-green-100 text-green-800'
      case 'checked_out': return 'bg-blue-100 text-blue-800'
      case 'scheduled': return 'bg-yellow-100 text-yellow-800'
      case 'cancelled': case 'no_show': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Visitor Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Pre-Register Visitor</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Visitors Today</div>
              <div className="text-2xl font-bold">{statistics.total_visitors_today}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Checked In</div>
              <div className="text-2xl font-bold text-green-600">{statistics.currently_checked_in}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Scheduled</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.scheduled_today}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">This Week</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.total_this_week}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active Badges</div>
              <div className="text-2xl font-bold">{statistics.active_badges}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Visitors</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Visitor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Host</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {visitors.slice(0, 12).map(vis => (
                  <tr key={vis.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{vis.visitor_name}</div>
                      <div className="text-xs text-gray-500">{vis.company || 'Individual'} • {vis.visit_type}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{vis.host_name}</div>
                      <div className="text-xs text-gray-500">{vis.location}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{vis.scheduled_date}</div>
                      <div className="text-xs text-gray-500">{vis.scheduled_time}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(vis.status)}`}>{vis.status.replace(/_/g, ' ')}</span>
                      {vis.badge_number && <div className="text-xs text-blue-600 mt-1">Badge: {vis.badge_number}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Access Logs</h3>
            <div className="space-y-3">
              {accessLogs.slice(0, 12).map(log => (
                <div key={log.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-mono text-sm">{log.badge_number}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${log.granted ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {log.granted ? 'Granted' : 'Denied'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">{log.visitor_name}</div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-xs text-gray-600">{log.access_point}</span>
                    <span className={`text-xs ${log.action === 'entry' ? 'text-green-600' : 'text-blue-600'}`}>{log.action}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
